import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import './EmployeeForm.css';
import supabase from '../../supabase';
import { useToast } from '../Invoice/Toast';

const EmployeeForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    emp_id: '',
    name: '',
    emp_role: '',
    joining_date: '',
    salary: '',
    location: '',
    image: null,
    pan_card: '',
    emp_bank_name: '',
    account_no: '',
    ifsc_code: '',
    pf_no: '',
    branch_name: '',
    earnings: [],
    deductions: [],
    custom_fields: []
  });

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Initialize toast hook
  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast();

  // Check authentication status on component mount
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error('Error getting user:', error);
          showError('Authentication Error', 'Failed to verify user authentication');
        }
        setUser(user);
      } catch (error) {
        console.error('Error in auth check:', error);
        showError('Authentication Error', 'An unexpected error occurred during authentication');
      } finally {
        setAuthLoading(false);
      }
    };

    getUser();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    // Validate file size (5MB limit)
    if (file && file.size > 5 * 1024 * 1024) {
      showError('File Too Large', 'Image size should be less than 5MB');
      e.target.value = '';
      return;
    }

    // Validate file type
    if (file && !file.type.startsWith('image/')) {
      showError('Invalid File Type', 'Please select a valid image file');
      e.target.value = '';
      return;
    }

    setFormData(prev => ({
      ...prev,
      image: file
    }));
  };

  const addKeyValueField = (section) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], { key: '', value: '' }]
    }));
  };

  const removeKeyValueField = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleKeyValueChange = (section, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const uploadImage = async (file) => {
    if (!file) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${formData.emp_id}-${Date.now()}.${fileExt}`;
      const filePath = `employee-images/${fileName}`;

      const { data, error } = await supabase.storage
        .from('employee-images')
        .upload(filePath, file);

      if (error) {
        console.error('Error uploading image:', error);
        showError('Upload Error', 'Failed to upload profile image');
        return null;
      }

      // Get the public URL
      const { data: publicData } = supabase.storage
        .from('employee-images')
        .getPublicUrl(filePath);

      return publicData.publicUrl;
    } catch (error) {
      console.error('Error in uploadImage:', error);
      showError('Upload Error', 'An unexpected error occurred while uploading image');
      return null;
    }
  };

  const validateForm = () => {
    // Basic required field validation
    if (!formData.emp_id.trim()) {
      showError('Validation Error', 'Employee ID is required');
      return false;
    }

    if (!formData.name.trim()) {
      showError('Validation Error', 'Employee name is required');
      return false;
    }

    if (!formData.emp_role.trim()) {
      showError('Validation Error', 'Employee role is required');
      return false;
    }

    if (!formData.joining_date) {
      showError('Validation Error', 'Joining date is required');
      return false;
    }

    if (!formData.salary || parseFloat(formData.salary) <= 0) {
      showError('Validation Error', 'Please enter a valid salary amount');
      return false;
    }

    if (!formData.location.trim()) {
      showError('Validation Error', 'Location is required');
      return false;
    }

    // Bank information validation
    if (!formData.pan_card.trim()) {
      showError('Validation Error', 'PAN card number is required');
      return false;
    }

    // PAN card format validation (basic)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(formData.pan_card.toUpperCase())) {
      showError('Validation Error', 'Invalid PAN card format (e.g., AAAAA0000A)');
      return false;
    }

    if (!formData.emp_bank_name.trim()) {
      showError('Validation Error', 'Bank name is required');
      return false;
    }

    if (!formData.account_no.trim()) {
      showError('Validation Error', 'Account number is required');
      return false;
    }

    if (!formData.ifsc_code.trim()) {
      showError('Validation Error', 'IFSC code is required');
      return false;
    }

    // IFSC code format validation (basic)
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(formData.ifsc_code.toUpperCase())) {
      showError('Validation Error', 'Invalid IFSC code format (e.g., SBIN0000123)');
      return false;
    }

    if (!formData.branch_name.trim()) {
      showError('Validation Error', 'Branch name is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Double-check user authentication
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !currentUser) {
        showError('Authentication Error', 'You must be logged in to add employees. Please log in and try again.');
        setLoading(false);
        return;
      }

      // Upload image first if provided
      let imageUrl = null;
      if (formData.image) {
        showInfo('Uploading', 'Uploading profile image...');
        imageUrl = await uploadImage(formData.image);
        if (!imageUrl) {
          setLoading(false);
          return;
        }
      }

      // Convert arrays to objects for JSONB storage
      const earningsObj = formData.earnings.reduce((acc, item) => {
        if (item.key && item.value) acc[item.key] = parseFloat(item.value) || 0;
        return acc;
      }, {});

      const deductionsObj = formData.deductions.reduce((acc, item) => {
        if (item.key && item.value) acc[item.key] = parseFloat(item.value) || 0;
        return acc;
      }, {});

      const customFieldsObj = formData.custom_fields.reduce((acc, item) => {
        if (item.key && item.value) acc[item.key] = item.value;
        return acc;
      }, {});

      const submitData = {
        emp_id: formData.emp_id.trim(),
        name: formData.name.trim(),
        emp_role: formData.emp_role.trim(),
        joining_date: formData.joining_date,
        salary: parseFloat(formData.salary) || 0,
        location: formData.location.trim(),
        pan_card: formData.pan_card.trim().toUpperCase(),
        emp_bank_name: formData.emp_bank_name.trim(),
        account_no: formData.account_no.trim(),
        ifsc_code: formData.ifsc_code.trim().toUpperCase(),
        pf_no: formData.pf_no.trim() || null,
        branch_name: formData.branch_name.trim(),
        earnings: earningsObj,
        deductions: deductionsObj,
        custom_fields: customFieldsObj,
        image: imageUrl,
        user_id: currentUser.id
      };

      console.log('Submitting data:', submitData);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('employees')
        .insert([submitData])
        .select();

      if (error) {
        throw error;
      }

      console.log('Data saved successfully:', data);
      showSuccess('Success', `Employee "${formData.name}" added successfully!`);
      
      // Reset form after successful submission
      setFormData({
        emp_id: '',
        name: '',
        emp_role: '',
        joining_date: '',
        salary: '',
        location: '',
        image: null,
        pan_card: '',
        emp_bank_name: '',
        account_no: '',
        ifsc_code: '',
        pf_no: '',
        branch_name: '',
        earnings: [],
        deductions: [],
        custom_fields: []
      });
      
      // Go back to dashboard after successful submission
      setTimeout(() => {
        if (onBack) {
          onBack();
        }
      }, 1500);
      
    } catch (error) {
      console.error('Error saving data:', error);
      showError('Save Error', error.message || 'Failed to save employee data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <>
        <ToastContainer />
        <div className="employeeForm-container">
          <div className="employeeForm-wrapper">
            <p>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  // Show login message if user is not authenticated
  if (!user) {
    return (
      <>
        <ToastContainer />
        <div className="employeeForm-container">
          <div className="employeeForm-wrapper">
            <div className="employeeForm-header">
              <h1 className="employeeForm-title">Employee Registration</h1>
              <button 
                onClick={onBack} 
                className="employeeForm-back-button"
                title="Back to Team Dashboard"
              >
                <ArrowLeft size={20} />
              </button>
            </div>
            <p>Please log in to access the employee registration form.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Toast Container */}
      <ToastContainer />

      <div className="employeeForm-container">
        <div className="employeeForm-wrapper">
          <div className="employeeForm-header">
            <h1 className="employeeForm-title">Employee Onboarding</h1>
            <button 
              onClick={onBack} 
              className="employeeForm-back-button"
              title="Back to Team Dashboard"
            >
              <ArrowLeft size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="employeeForm-form">
            {/* Employee Info Section */}
            <div className="employeeForm-section">
              <h2 className="employeeForm-section-title">Employee Information</h2>
              <div className="employeeForm-grid">
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Employee ID *</label>
                  <input
                    type="text"
                    name="emp_id"
                    value={formData.emp_id}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Employee Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Role *</label>
                  <input
                    type="text"
                    name="emp_role"
                    value={formData.emp_role}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Joining Date *</label>
                  <input
                    type="date"
                    name="joining_date"
                    value={formData.joining_date}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Salary *</label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    placeholder="0.00"
                    step="0.01"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field employeeForm-field-full">
                  <label className="employeeForm-label">Profile Image</label>
                  <input
                    type="file"
                    name="image"
                    onChange={handleImageChange}
                    className="employeeForm-file-input"
                    accept="image/*"
                  />
                  {formData.image && (
                    <p className="employeeForm-file-name">{formData.image.name}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Bank Info Section */}
            <div className="employeeForm-section">
              <h2 className="employeeForm-section-title">Bank Information</h2>
              <div className="employeeForm-grid">
                <div className="employeeForm-field">
                  <label className="employeeForm-label">PAN Card *</label>
                  <input
                    type="text"
                    name="pan_card"
                    value={formData.pan_card}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    placeholder="AAAAA0000A"
                    maxLength="10"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Bank Name *</label>
                  <input
                    type="text"
                    name="emp_bank_name"
                    value={formData.emp_bank_name}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Account Number *</label>
                  <input
                    type="text"
                    name="account_no"
                    value={formData.account_no}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">IFSC Code *</label>
                  <input
                    type="text"
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    placeholder="SBIN0000123"
                    maxLength="11"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">Branch Name *</label>
                  <input
                    type="text"
                    name="branch_name"
                    value={formData.branch_name}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                    required
                  />
                </div>
                <div className="employeeForm-field">
                  <label className="employeeForm-label">PF Number</label>
                  <input
                    type="text"
                    name="pf_no"
                    value={formData.pf_no}
                    onChange={handleInputChange}
                    className="employeeForm-input"
                  />
                </div>
              </div>
            </div>

            {/* Salary Section */}
            <div className="employeeForm-section">
              <h2 className="employeeForm-section-title">Salary Details</h2>
              <div className="employeeForm-salary-container">
                {/* Earnings */}
                <div className="employeeForm-salary-subsection">
                  <div className="employeeForm-subsection-header">
                    <h3 className="employeeForm-subsection-title">Earnings</h3>
                    <button
                      type="button"
                      onClick={() => addKeyValueField('earnings')}
                      className="employeeForm-add-btn"
                    >
                      + Add Field
                    </button>
                  </div>
                  <div className="employeeForm-key-value-container">
                    {formData.earnings.map((item, index) => (
                      <div key={index} className="employeeForm-key-value-row">
                        <input
                          type="text"
                          placeholder="Field Name"
                          value={item.key}
                          onChange={(e) => handleKeyValueChange('earnings', index, 'key', e.target.value)}
                          className="employeeForm-key-input"
                        />
                        <input
                          type="number"
                          placeholder="Amount"
                          value={item.value}
                          onChange={(e) => handleKeyValueChange('earnings', index, 'value', e.target.value)}
                          className="employeeForm-value-input"
                          step="0.01"
                        />
                        <button
                          type="button"
                          onClick={() => removeKeyValueField('earnings', index)}
                          className="employeeForm-remove-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {formData.earnings.length === 0 && (
                      <p className="employeeForm-empty-state">No earnings added yet</p>
                    )}
                  </div>
                </div>

                {/* Deductions */}
                <div className="employeeForm-salary-subsection">
                  <div className="employeeForm-subsection-header">
                    <h3 className="employeeForm-subsection-title">Deductions</h3>
                    <button
                      type="button"
                      onClick={() => addKeyValueField('deductions')}
                      className="employeeForm-add-btn"
                    >
                      + Add Field
                    </button>
                  </div>
                  <div className="employeeForm-key-value-container">
                    {formData.deductions.map((item, index) => (
                      <div key={index} className="employeeForm-key-value-row">
                        <input
                          type="text"
                          placeholder="Field Name"
                          value={item.key}
                          onChange={(e) => handleKeyValueChange('deductions', index, 'key', e.target.value)}
                          className="employeeForm-key-input"
                        />
                        <input
                          type="number"
                          placeholder="Amount"
                          value={item.value}
                          onChange={(e) => handleKeyValueChange('deductions', index, 'value', e.target.value)}
                          className="employeeForm-value-input"
                          step="0.01"
                        />
                        <button
                          type="button"
                          onClick={() => removeKeyValueField('deductions', index)}
                          className="employeeForm-remove-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {formData.deductions.length === 0 && (
                      <p className="employeeForm-empty-state">No deductions added yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Fields Section */}
            <div className="employeeForm-section">
              <div className="employeeForm-subsection-header">
                <h2 className="employeeForm-section-title">Custom Fields</h2>
                <button
                  type="button"
                  onClick={() => addKeyValueField('custom_fields')}
                  className="employeeForm-add-btn"
                >
                  + Add Field
                </button>
              </div>
              <div className="employeeForm-key-value-container">
                {formData.custom_fields.map((item, index) => (
                  <div key={index} className="employeeForm-key-value-row">
                    <input
                      type="text"
                      placeholder="Field Name"
                      value={item.key}
                      onChange={(e) => handleKeyValueChange('custom_fields', index, 'key', e.target.value)}
                      className="employeeForm-key-input"
                    />
                    <input
                      type="text"
                      placeholder="Field Value"
                      value={item.value}
                      onChange={(e) => handleKeyValueChange('custom_fields', index, 'value', e.target.value)}
                      className="employeeForm-value-input"
                    />
                    <button
                      type="button"
                      onClick={() => removeKeyValueField('custom_fields', index)}
                      className="employeeForm-remove-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {formData.custom_fields.length === 0 && (
                  <p className="employeeForm-empty-state">No custom fields added yet</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="employeeForm-submit-container">
              <button
                type="submit"
                disabled={loading}
                className="employeeForm-submit-btn"
              >
                {loading ? 'Saving...' : 'Save Employee Details'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EmployeeForm;